import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Lock, Plus, Trash2, Package, MessageSquare, Handshake, ImageIcon, LogOut, Eye, EyeOff, Upload, Check, Loader2, Calculator, FlaskConical, Clock, BarChart3, FileText, ShoppingCart, Crown, Utensils, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminBlogsTab from '../components/admin/AdminBlogsTab';
import AdminOrdersTab from '../components/admin/AdminOrdersTab';
import AdminTimelineTab from '../components/admin/AdminTimelineTab';
import AdminMetricsTab from '../components/admin/AdminMetricsTab';
import AdminProductsTab from '../components/admin/AdminProductsTab';
import AdminMembersTab from '../components/admin/AdminMembersTab';
import AdminCateringTab from '../components/admin/AdminCateringTab';
import AdminCareersTab from '../components/admin/AdminCareersTab';

const API = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [isSetup, setIsSetup] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [siteImages, setSiteImages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [timelineForm, setTimelineForm] = useState({ date: '', heading: '', subheading: '' });
  const [metrics, setMetrics] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [savedImages, setSavedImages] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [testimonialForm, setTestimonialForm] = useState({ name: '', role: '', image: '', content: '', rating: 5 });
  const [partnerForm, setPartnerForm] = useState({ name: '', logo: '' });

  const fileInputRefs = useRef({});

  const showMsg = (msg, type = 'success') => {
    if (type === 'error') { setError(msg); setTimeout(() => setError(''), 3000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
  };

  const authHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });

  const fetchData = useCallback(async () => {
    try {
      const [p, t, pr] = await Promise.all([
        fetch(`${API}/api/products`).then(r => r.json()),
        fetch(`${API}/api/testimonials`).then(r => r.json()),
        fetch(`${API}/api/partners`).then(r => r.json())
      ]);
      setProducts(p); setTestimonials(t); setPartners(pr);
    } catch (err) { console.error('Fetch error:', err); }
  }, []);

  const fetchImages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/site-images`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSiteImages(await res.json());
    } catch (err) { console.error('Fetch images error:', err); }
  }, [token]);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/timeline`);
      if (res.ok) setTimeline(await res.json());
    } catch (err) { console.error('Fetch timeline error:', err); }
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metrics`);
      if (res.ok) setMetrics(await res.json());
    } catch (err) { console.error('Fetch metrics error:', err); }
  }, []);

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/blogs`);
      if (res.ok) setBlogs(await res.json());
    } catch (err) { console.error('Fetch blogs error:', err); }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/orders`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setOrders(await res.json());
    } catch (err) { console.error('Fetch orders error:', err); }
  }, [token]);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/membership/members`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setMembers(await res.json());
    } catch (err) { console.error('Fetch members error:', err); }
  }, [token]);

  useEffect(() => {
    fetch(`${API}/api/auth/check`).then(r => r.json()).then(d => setIsSetup(d.admin_exists));
    if (token) { fetchData(); fetchImages(); fetchTimeline(); fetchMetrics(); fetchBlogs(); fetchOrders(); fetchMembers(); }
  }, [token, fetchData, fetchImages, fetchTimeline, fetchMetrics, fetchBlogs, fetchOrders, fetchMembers]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}${isSetup ? '/api/auth/login' : '/api/auth/setup'}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(loginForm) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Auth failed');
      sessionStorage.setItem('admin_token', data.access_token);
      setToken(data.access_token);
      showMsg(isSetup ? 'Logged in!' : 'Admin created!');
    } catch (err) { showMsg(err.message, 'error'); }
    setLoading(false);
  };

  const handleLogout = () => { sessionStorage.removeItem('admin_token'); setToken(''); };

  const createItem = async (type, formData, resetFn) => {
    setLoading(true);
    try {
      const body = type === 'products' ? { ...formData, price: parseFloat(formData.price) } : formData;
      const res = await fetch(`${API}/api/admin/${type}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      showMsg(`${type.slice(0, -1)} added!`);
      resetFn();
      fetchData();
    } catch (err) {
      if (err.message.includes('token') || err.message.includes('401')) handleLogout();
      else showMsg(err.message, 'error');
    }
    setLoading(false);
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API}/api/admin/${type}/${id}`, { method: 'DELETE', headers: authHeaders() });
      showMsg('Deleted!');
      fetchData();
    } catch (err) { showMsg('Delete failed', 'error'); }
  };

  // Upload image and update site-image
  const handleImageUpload = async (slug, file) => {
    if (!file) return;
    setUploadingSlug(slug);
    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${API}/api/upload`, { method: 'POST', body: formData });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { path } = await uploadRes.json();

      // Build the serve URL
      const imageUrl = `${API}/api/files/${path}`;

      // Update site image with serve URL
      const updateRes = await fetch(`${API}/api/admin/site-images/${slug}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ url: imageUrl })
      });
      if (!updateRes.ok) throw new Error('Update failed');

      setSavedImages(p => ({ ...p, [slug]: true }));
      setTimeout(() => setSavedImages(p => ({ ...p, [slug]: false })), 2500);
      fetchImages();
      showMsg('Image uploaded & saved!');
    } catch (err) {
      showMsg(err.message, 'error');
    }
    setUploadingSlug(null);
  };

  // Upload for form fields (products, testimonials, partners)
  const handleFormImageUpload = async (file, setter, field) => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/upload`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const { path } = await res.json();
      const imageUrl = `${API}/api/files/${path}`;
      setter(f => ({ ...f, [field]: imageUrl }));
      showMsg('Image uploaded!');
    } catch (err) { showMsg('Upload failed', 'error'); }
    setLoading(false);
  };

  const seedImages = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/admin/site-images/seed`, { method: 'POST', headers: authHeaders() });
      fetchImages();
      showMsg('Default images loaded!');
    } catch (err) { showMsg('Seed failed', 'error'); }
    setLoading(false);
  };

  // Login screen
  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 pt-20" data-testid="admin-login-page">
        <Card className="w-full max-w-md shadow-xl border border-[#E8DFD3]">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-[#5C7A3A]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Lock size={24} className="text-[#5C7A3A]" />
              </div>
              <h1 className="text-2xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {isSetup ? 'Admin Login' : 'Create Admin Account'}
              </h1>
              <p className="text-sm text-[#7a6555] mt-1">{isSetup ? 'Sign in to manage your content' : 'Set up your first admin account'}</p>
            </div>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg mb-4" data-testid="auth-error">{error}</div>}
            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-2 rounded-lg mb-4" data-testid="auth-success">{success}</div>}
            <form onSubmit={handleAuth} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2C1810] mb-1 block">Username</label>
                <Input data-testid="admin-username-input" value={loginForm.username} onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))} placeholder="admin" required />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C1810] mb-1 block">Password</label>
                <div className="relative">
                  <Input data-testid="admin-password-input" type={showPassword ? 'text' : 'password'} value={loginForm.password} onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} placeholder="Enter password" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999]">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-semibold py-5 cursor-pointer">
                {loading ? 'Please wait...' : (isSetup ? 'Sign In' : 'Create Account')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group images by page
  const imagesByPage = {};
  siteImages.forEach(img => {
    if (!imagesByPage[img.page]) imagesByPage[img.page] = [];
    imagesByPage[img.page].push(img);
  });

  const tabs = [
    { key: 'products', label: 'Products', icon: <Package size={16} />, count: products.length },
    { key: 'testimonials', label: 'Testimonials', icon: <MessageSquare size={16} />, count: testimonials.length },
    { key: 'partners', label: 'Partners', icon: <Handshake size={16} />, count: partners.length },
    { key: 'images', label: 'Images', icon: <ImageIcon size={16} />, count: siteImages.length },
    { key: 'timeline', label: 'Timeline', icon: <Clock size={16} />, count: timeline.length },
    { key: 'metrics', label: 'Metrics', icon: <BarChart3 size={16} />, count: metrics.length },
    { key: 'blogs', label: 'Blogs', icon: <FileText size={16} />, count: blogs.length },
    { key: 'orders', label: 'Orders', icon: <ShoppingCart size={16} />, count: orders.length },
    { key: 'members', label: 'Members', icon: <Crown size={16} />, count: members.length },
    { key: 'catering', label: 'Catering', icon: <Utensils size={16} /> },
    { key: 'careers', label: 'Careers', icon: <Briefcase size={16} /> }
  ];

  // Reusable upload button for form fields
  const UploadButton = ({ onUpload, label = 'Upload Image', currentUrl }) => {
    const ref = useRef(null);
    return (
      <div className="space-y-2">
        {currentUrl && (
          <div className="relative h-24 rounded-lg overflow-hidden bg-gray-50">
            <img src={currentUrl} alt="Preview" className="w-full h-full object-cover" />
          </div>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ''; }} />
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} className="w-full border-dashed border-2 border-[#D4A574] text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer" disabled={loading}>
          <Upload size={16} className="mr-2" /> {loading ? 'Uploading...' : label}
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0] pt-24 pb-12" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Panel</h1>
            <p className="text-sm text-[#7a6555]">Manage products, testimonials, partners & images</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/calculator')} variant="outline" data-testid="admin-calculator-btn" className="border-[#D4A574] text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer">
              <Calculator size={16} className="mr-2" /> Baking Calculator
            </Button>
            <Button onClick={() => navigate('/admin/formulation')} variant="outline" data-testid="admin-formulation-btn" className="border-[#D4A574] text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer">
              <FlaskConical size={16} className="mr-2" /> Formulation Calculator
            </Button>
            <Button onClick={handleLogout} variant="outline" data-testid="admin-logout-btn" className="border-red-200 text-red-600 hover:bg-red-50 cursor-pointer">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg mb-4">{error}</div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-2 rounded-lg mb-4">{success}</div>}

        <div className="flex gap-2 mb-6 bg-white p-1 rounded-xl border border-[#E8DFD3] w-fit">
          {tabs.map(tab => (
            <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key ? 'bg-[#5C7A3A] text-white shadow-sm' : 'text-[#7a6555] hover:bg-gray-50'
              }`}>
              {tab.icon} {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'}`}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* ===== IMAGES TAB ===== */}
        {activeTab === 'images' && (
          <div className="space-y-8">
            {siteImages.length === 0 ? (
              <div className="text-center py-16" data-testid="no-images">
                <ImageIcon size={48} className="mx-auto text-[#D4A574] mb-4" />
                <p className="text-lg text-[#2C1810] font-semibold mb-2">No images configured yet</p>
                <p className="text-sm text-[#7a6555] mb-6">Load default images to get started</p>
                <Button onClick={seedImages} disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="seed-images-btn">
                  {loading ? 'Loading...' : 'Load Default Images'}
                </Button>
              </div>
            ) : (
              Object.entries(imagesByPage).map(([page, imgs]) => (
                <div key={page}>
                  <h3 className="text-lg font-bold text-[#2C1810] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <div className="w-1 h-5 bg-[#5C7A3A] rounded-full"></div>
                    {page}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {imgs.map(img => (
                      <Card key={img.slug} className="border border-[#E8DFD3] shadow-sm overflow-hidden bg-white" data-testid={`image-card-${img.slug}`}>
                        <div className="relative h-40 overflow-hidden bg-gray-50">
                          <img
                            src={img.url}
                            alt={img.label}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          {savedImages[img.slug] && (
                            <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                              <div className="bg-green-500 text-white rounded-full p-2"><Check size={20} /></div>
                            </div>
                          )}
                          {uploadingSlug === img.slug && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Loader2 size={28} className="text-white animate-spin" />
                            </div>
                          )}
                        </div>
                        <CardContent className="p-4">
                          <p className="text-xs font-semibold text-[#C4844A] mb-0.5">{img.slug}</p>
                          <p className="text-sm font-medium text-[#2C1810] mb-3">{img.label}</p>
                          <input
                            ref={el => { fileInputRefs.current[img.slug] = el; }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={e => { if (e.target.files[0]) handleImageUpload(img.slug, e.target.files[0]); e.target.value = ''; }}
                          />
                          <Button
                            onClick={() => fileInputRefs.current[img.slug]?.click()}
                            disabled={uploadingSlug === img.slug}
                            data-testid={`upload-image-${img.slug}`}
                            className="w-full border-dashed border-2 border-[#D4A574] bg-transparent text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer font-medium text-sm"
                            variant="outline"
                          >
                            {uploadingSlug === img.slug ? (
                              <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading...</>
                            ) : (
                              <><Upload size={14} className="mr-2" /> Upload New Image</>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== TIMELINE TAB ===== */}
        {activeTab === 'timeline' && (
          <AdminTimelineTab
            timeline={timeline} fetchTimeline={fetchTimeline}
            authHeaders={authHeaders} showMsg={showMsg}
            loading={loading} setLoading={setLoading}
          />
        )}

        {/* ===== METRICS TAB ===== */}
        {activeTab === 'metrics' && (
          <AdminMetricsTab
            metrics={metrics} fetchMetrics={fetchMetrics}
            authHeaders={authHeaders} showMsg={showMsg}
            loading={loading} setLoading={setLoading}
          />
        )}

        {/* ===== BLOGS TAB ===== */}
        {activeTab === 'blogs' && (
          <AdminBlogsTab
            blogs={blogs} fetchBlogs={fetchBlogs}
            authHeaders={authHeaders} showMsg={showMsg}
            loading={loading} setLoading={setLoading}
            UploadButton={UploadButton} handleFormImageUpload={handleFormImageUpload}
          />
        )}

        {/* ===== ORDERS TAB ===== */}
        {activeTab === 'orders' && <AdminOrdersTab orders={orders} />}

        {/* ===== MEMBERS TAB ===== */}
        {activeTab === 'members' && <AdminMembersTab members={members} />}

        {/* ===== CATERING TAB ===== */}
        {activeTab === 'catering' && (
          <AdminCateringTab authHeaders={authHeaders} showMsg={showMsg} loading={loading} setLoading={setLoading} />
        )}

        {/* ===== CAREERS TAB ===== */}
        {activeTab === 'careers' && (
          <AdminCareersTab authHeaders={authHeaders} showMsg={showMsg} loading={loading} setLoading={setLoading} token={token} />
        )}

        {/* ===== PRODUCTS TAB ===== */}
        {activeTab === 'products' && (
          <AdminProductsTab
            products={products} fetchData={fetchData}
            authHeaders={authHeaders} showMsg={showMsg}
            loading={loading} setLoading={setLoading}
            UploadButton={UploadButton} handleFormImageUpload={handleFormImageUpload}
          />
        )}

        {/* ===== OTHER TABS (Testimonials, Partners) ===== */}
        {(activeTab === 'testimonials' || activeTab === 'partners') && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-1 shadow-sm border border-[#E8DFD3] h-fit bg-white">
              <CardContent className="p-5">
                <h3 className="font-bold text-[#2C1810] mb-4 flex items-center gap-2">
                  <Plus size={16} className="text-[#5C7A3A]" /> Add {activeTab.slice(0, -1)}
                </h3>

                {activeTab === 'testimonials' && (
                  <form onSubmit={e => { e.preventDefault(); createItem('testimonials', testimonialForm, () => setTestimonialForm({ name: '', role: '', image: '', content: '', rating: 5 })); }} className="space-y-3">
                    <Input data-testid="testimonial-name-input" placeholder="Person name" value={testimonialForm.name} onChange={e => setTestimonialForm(f => ({ ...f, name: e.target.value }))} required />
                    <Input data-testid="testimonial-role-input" placeholder="Role (e.g., Farmer Partner)" value={testimonialForm.role} onChange={e => setTestimonialForm(f => ({ ...f, role: e.target.value }))} required />
                    <UploadButton currentUrl={testimonialForm.image} onUpload={(file) => handleFormImageUpload(file, setTestimonialForm, 'image')} label="Upload Photo" />
                    <Textarea data-testid="testimonial-content-input" placeholder="Testimonial content" value={testimonialForm.content} onChange={e => setTestimonialForm(f => ({ ...f, content: e.target.value }))} required />
                    <select data-testid="testimonial-rating-select" value={testimonialForm.rating} onChange={e => setTestimonialForm(f => ({ ...f, rating: parseInt(e.target.value) }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                      {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                    </select>
                    <Button type="submit" data-testid="add-testimonial-btn" disabled={loading || !testimonialForm.image} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">{loading ? 'Adding...' : 'Add Testimonial'}</Button>
                  </form>
                )}

                {activeTab === 'partners' && (
                  <form onSubmit={e => { e.preventDefault(); createItem('partners', partnerForm, () => setPartnerForm({ name: '', logo: '' })); }} className="space-y-3">
                    <Input data-testid="partner-name-input" placeholder="Partner name" value={partnerForm.name} onChange={e => setPartnerForm(f => ({ ...f, name: e.target.value }))} required />
                    <UploadButton currentUrl={partnerForm.logo} onUpload={(file) => handleFormImageUpload(file, setPartnerForm, 'logo')} label="Upload Logo" />
                    <Button type="submit" data-testid="add-partner-btn" disabled={loading || !partnerForm.logo} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer">{loading ? 'Adding...' : 'Add Partner'}</Button>
                  </form>
                )}
              </CardContent>
            </Card>

            <div className="lg:col-span-2 space-y-3">
              {activeTab === 'testimonials' && (
                testimonials.length === 0
                  ? <div className="text-center py-12 text-[#7a6555]" data-testid="no-testimonials">No testimonials yet. Add your first testimonial.</div>
                  : testimonials.map(item => (
                    <Card key={item.id} className="shadow-sm border border-[#E8DFD3] bg-white" data-testid={`testimonial-item-${item.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <img src={item.image} alt={item.name} className="w-14 h-14 rounded-full object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#2C1810]">{item.name}</p>
                          <p className="text-xs text-[#5C7A3A]">{item.role}</p>
                          <p className="text-sm text-[#7a6555] truncate italic">"{item.content}"</p>
                        </div>
                        <Button onClick={() => deleteItem('testimonials', item.id)} variant="ghost" size="sm" data-testid={`delete-testimonial-${item.id}`} className="text-red-500 hover:bg-red-50 cursor-pointer"><Trash2 size={16} /></Button>
                      </CardContent>
                    </Card>
                  ))
              )}
              {activeTab === 'partners' && (
                partners.length === 0
                  ? <div className="text-center py-12 text-[#7a6555]" data-testid="no-partners">No partners yet. Add your first partner.</div>
                  : partners.map(item => (
                    <Card key={item.id} className="shadow-sm border border-[#E8DFD3] bg-white" data-testid={`partner-item-${item.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <img src={item.logo} alt={item.name} className="w-14 h-10 object-contain bg-gray-50 rounded p-1" />
                        <div className="flex-1"><p className="font-bold text-[#2C1810]">{item.name}</p></div>
                        <Button onClick={() => deleteItem('partners', item.id)} variant="ghost" size="sm" data-testid={`delete-partner-${item.id}`} className="text-red-500 hover:bg-red-50 cursor-pointer"><Trash2 size={16} /></Button>
                      </CardContent>
                    </Card>
                  ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
